import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type AppsSessionResult, createAppsSession } from '../api/createAppsSession';

export const appsSessionQueryKey = (appsOrigin: string | undefined) =>
    ['app-platform', 'apps-session', appsOrigin] as const;

/**
 * Mints the apps-host cookie session for `appsOrigin`, once per page.
 *
 * Keyed on the origin and shared through the query cache, so every slot on a page
 * observes the same session and — the point of routing it through react-query —
 * concurrent `refetch()` calls collapse into one in-flight POST. A page with six
 * widgets whose cookie expires at once re-mints the session once, not six times.
 *
 * Pass `undefined` while the origin is still unknown (the assignments response
 * carries it) or when a page has no widgets to render: the query stays disabled
 * and no request is made.
 */
export function useAppsSession(appsOrigin: string | undefined): UseQueryResult<AppsSessionResult, Error> {
    return useQuery({
        queryKey: appsSessionQueryKey(appsOrigin),
        queryFn: async ({ signal }) => {
            if (appsOrigin === undefined) {
                throw new Error('No apps origin to open a session against');
            }
            return createAppsSession(appsOrigin, { signal });
        },
        enabled: appsOrigin !== undefined,
        // The cookie's lifetime is the apps host's business, and it tells us when
        // it ends (lumi:apps_session_expired). Nothing is gained by re-minting on
        // a schedule of our own.
        staleTime: Infinity,
        retry: false,
    });
}
